import { riskLevelFromScore } from '../chunk-6B5KO6TH.js';
import '../chunk-PZ5AY32C.js';

// src/ml/model.ts
var MLRole = {
  /** Live model whose output is used. */
  CHAMPION: "champion",
  /** Runs in parallel for comparison; output not used. */
  SHADOW: "shadow",
  /** Gradual rollout slice. */
  CANARY: "canary"
};
var MLStage = {
  DEV: "dev",
  STAGING: "staging",
  PRODUCTION: "production",
  RETIRED: "retired"
};
var MLTask = {
  RISK_SCORE: "risk_score",
  CLASSIFICATION: "classification",
  ENTITY_RESOLUTION: "entity_resolution",
  SUMMARIZATION: "summarization",
  ANOMALY: "anomaly"
};
function modelVersionKey(model) {
  return `${model.id}@${model.version}`;
}
function championOf(models) {
  return models.find((m) => m.role === MLRole.CHAMPION) ?? models[0];
}
function challengersOf(models) {
  return models.filter((m) => m.role !== MLRole.CHAMPION);
}

// src/ml/prediction.ts
function predictionRiskLevel(prediction, bands) {
  return riskLevelFromScore(prediction.score, bands);
}
function topContributions(prediction, n = 5) {
  return [...prediction.contributions ?? []].sort((a, b) => Math.abs(b.weight) - Math.abs(a.weight)).slice(0, n);
}
function predictionTargetKey(prediction) {
  const { kind, id, chain } = prediction.target;
  return chain ? `${kind}:${chain}:${id}` : `${kind}:${id}`;
}

// src/ml/review.ts
var MLReviewStatus = {
  PENDING: "pending",
  APPROVED: "approved",
  REJECTED: "rejected",
  ESCALATED: "escalated"
};
var ML_REVIEW_STATUS_LABEL = {
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
  escalated: "Escalated"
};
var MLReviewReason = {
  LOW_CONFIDENCE: "low_confidence",
  HIGH_RISK: "high_risk",
  SHADOW_DISAGREEMENT: "shadow_disagreement",
  SAMPLED: "sampled",
  MANUAL: "manual"
};
function isReviewActionable(item) {
  return item.status === MLReviewStatus.PENDING || item.status === MLReviewStatus.ESCALATED;
}
function pendingReviewCount(items) {
  return items.filter(isReviewActionable).length;
}
function applyReviewDecision(item, decision) {
  return {
    ...item,
    status: decision.status,
    decidedBy: decision.decidedBy,
    rationale: decision.rationale,
    decidedAt: decision.at ?? (/* @__PURE__ */ new Date()).toISOString()
  };
}

// src/ml/ab.ts
function comparePredictions(champion, shadow, bands) {
  const shadowByTarget = new Map(shadow.map((p) => [predictionTargetKey(p), p]));
  const seen = /* @__PURE__ */ new Set();
  const pairs = [];
  for (const champ of champion) {
    const key = predictionTargetKey(champ);
    seen.add(key);
    const shad = shadowByTarget.get(key);
    if (!shad) continue;
    const championLevel = predictionRiskLevel(champ, bands);
    const shadowLevel = predictionRiskLevel(shad, bands);
    pairs.push({
      targetKey: key,
      champion: champ,
      shadow: shad,
      agree: championLevel === shadowLevel,
      championLevel,
      shadowLevel,
      scoreDelta: champ.score != null && shad.score != null ? champ.score - shad.score : null
    });
  }
  const championOnly = champion.length - pairs.length;
  const shadowOnly = shadow.filter((p) => !seen.has(predictionTargetKey(p))).length;
  const agreed = pairs.filter((p) => p.agree).length;
  return {
    championKey: champion[0] ? `${champion[0].modelId}@${champion[0].modelVersion}` : "champion",
    shadowKey: shadow[0] ? `${shadow[0].modelId}@${shadow[0].modelVersion}` : "shadow",
    pairs,
    championOnly,
    shadowOnly,
    agreementRate: pairs.length ? agreed / pairs.length : 1
  };
}
function disagreements(comparison) {
  return comparison.pairs.filter((p) => !p.agree);
}

// src/ml/display.ts
function modelLabel(model) {
  const name = model.modelName?.trim();
  if (!name) return null;
  const version = model.modelVersion?.trim();
  return version ? `${name} ${version}` : name;
}
function modelVariantLabel(variant) {
  const text = variant?.trim();
  return text ? text.replace(/-/, " ") : null;
}
function formatModelScore(score) {
  if (!Number.isFinite(score)) return "";
  if (score >= 0 && score <= 1) return `${Number((score * 100).toFixed(1))}%`;
  return String(Number(score.toFixed(4)));
}
function modelReasonText(code) {
  const words = code.replace(/[_:.-]+/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : "";
}

export { MLReviewReason, MLReviewStatus, MLRole, MLStage, MLTask, ML_REVIEW_STATUS_LABEL, applyReviewDecision, challengersOf, championOf, comparePredictions, disagreements, formatModelScore, isReviewActionable, modelLabel, modelReasonText, modelVariantLabel, modelVersionKey, pendingReviewCount, predictionRiskLevel, predictionTargetKey, topContributions };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map